import {JwtToken} from '../store/user/types';

/**
 * Pluggable token refresh (FE #218).
 *
 * By default useApiClient refreshes in JS: it POSTs `auth/token/refresh/`
 * itself, single-flighted within this JS context. That is enough when this JS
 * context is the only party rotating the refresh token. On mobile it is not:
 * the iOS widget extension and the Android widget worker rotate the SAME token
 * from outside it, so the app installs a refresher backed by the native
 * implementation the widgets share. That implementation coordinates every
 * rotator on the device through a lease in a shared record
 * (apps/mobile/docs/session-token-protocol.md).
 *
 * Core keeps the policy: what each outcome does to the session (store the new
 * pair, log out, or surface an error and leave the session alone). The
 * refresher only reports what happened.
 */

/** Failure kinds that leave the session alive. Each names a `network` message. */
export type TransientRefreshCode =
  | 'connectionError'
  | 'timeoutError'
  | 'serverError'
  | 'unknownError';

export type TokenRefreshResult =
  /**
   * A pair whose access token is valid for at least `minValiditySeconds`.
   * It may be one another rotator already minted, in which case no request
   * was made.
   */
  | {status: 'ok'; token: JwtToken}
  /** The session was signed out (the shared record holds a tombstone). */
  | {status: 'signedOut'}
  /** The refresh token is past its `exp`, judged locally before any request. */
  | {status: 'expired'}
  /**
   * The backend refused the refresh token while this device was its only
   * presenter, and it is still the token of record: bad signature, revoked,
   * or inactive user. Terminal. `details` carries the server's message, when
   * there was one.
   */
  | {status: 'rejected'; details?: {message: string; code: string}}
  /** Nothing was decided about the session: network, 5xx, timeout, storage. */
  | {status: 'transient'; code: TransientRefreshCode};

export type TokenRefreshRequest = {
  /**
   * The pair this JS context holds. The refresher decides from its own token of
   * record, which another rotator may have moved past this pair. It uses this
   * pair only to seed a record that does not exist yet.
   */
  current: JwtToken;
  /** An access token with less validity left than this is refreshed. */
  minValiditySeconds: number;
  /** Sent as Accept-Language, so localized error messages match the app. */
  language: string;
};

/**
 * A refresher must settle, must not throw, and must report its own failures
 * (core cannot tell a broken native bridge from a flaky network). As a last
 * resort core treats a throw as `transient`, and a refresher that has not
 * settled after this long as a `timeoutError`. That leaves room for the native
 * worst case: a wait for another rotator of up to 35 s, then a request of up
 * to 15 s.
 */
export type TokenRefresher = (
  request: TokenRefreshRequest,
) => Promise<TokenRefreshResult>;

export const REFRESHER_TIMEOUT_MS = 60_000;

let configuredRefresher: TokenRefresher | null = null;

/**
 * Install the refresher useApiClient uses instead of its built-in JS refresh.
 * Mobile-only today; pass `null` to restore the built-in path.
 */
export const configureTokenRefresher = (refresher: TokenRefresher | null) => {
  configuredRefresher = refresher;
};

export const getTokenRefresher = (): TokenRefresher | null =>
  configuredRefresher;
